/* Grabatee USDOT Decal Studio — v1.1 (2026-08-28)
   v1.1: free downloads carry attribution — PNG gains tEXt metadata (Software/
   Comment; pixels unchanged), SVG gains a deletable #Info credit layer BELOW
   the decal (outside the cut), filenames gain a grabatee- prefix. The order
   hand-off PNG and payload are untouched.
   ─────────────────────────────────────────────────────────────────────────────
   Client-side USDOT number decal designer. Mirrors qr-studio.js:
     • All layout math is done in INCHES, then scaled to the preview canvas
       or to 300 DPI for export — so the cap height shown is the real one.
     • 49 CFR 390.21 checks: legal/trade name present, "USDOT" precedes the
       number, sharp contrast (WCAG luminance ratio), legible at 50 ft
       (industry 2" cap-height standard, 1.5" hard floor for ordering).
     • "Order" rasterizes at 300 DPI → sessionStorage("grabUsdotHandoff")
       → navigates to the USDOT decal product page, where usdot-handoff.js
       feeds the PNG into the unchanged sticker customizer.
   Requires: sections/grab-usdot-studio.liquid, assets/usdot-studio.css.
   ───────────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  /* ══ 1. CONFIG ══ */
  const SIZES = [
    { value: "18x6", w: 18, h: 6 },
    { value: "24x8", w: 24, h: 8 },
    { value: "24x12", w: 24, h: 12 },
    { value: "30x10", w: 30, h: 10 },
    { value: "36x12", w: 36, h: 12 }
  ];
  const FONTS = [
    { id: "impact", label: "Tall", sample: "USDOT", family: '"Impact","Haettenschweiler","Arial Narrow Bold",sans-serif', weight: "400", note: "Condensed — biggest letters per inch of width" },
    { id: "block", label: "Block", sample: "USDOT", family: '"Arial Black","Arial Bold",Arial,Helvetica,sans-serif', weight: "900", note: "Heavy and wide — the classic DOT look" },
    { id: "standard", label: "Standard", sample: "USDOT", family: 'Arial,Helvetica,sans-serif', weight: "700", note: "Clean bold sans" }
  ];
  const INK_SWATCHES = [
    { hex: "#000000", name: "Black" }, { hex: "#FFFFFF", name: "White" },
    { hex: "#1E3A8A", name: "Navy" }, { hex: "#B91C1C", name: "Red" },
    { hex: "#FACC15", name: "Yellow" }, { hex: "#00AEEF", name: "Grabatee cyan" }
  ];
  const BG_SWATCHES = [
    { hex: "#FFFFFF", name: "White" }, { hex: "#000000", name: "Black" },
    { hex: "#FACC15", name: "Yellow" }, { hex: "#F97316", name: "Safety orange" },
    { hex: "#1E3A8A", name: "Navy" }, { hex: "#9CA3AF", name: "Silver" }
  ];
  const SAFE_IN = 0.4;          /* safe margin inside the cut, inches */
  const LINE_GAP = 0.22;        /* gap between lines as a fraction of cap height */
  const HALO_IN = 0.3;          /* contour mode: halo of background color around letters */
  const CAP_STANDARD = 2.0;     /* industry "2-inch letters" standard */
  const CAP_FLOOR = 1.5;        /* below this the studio refuses to order */
  const CONTRAST_MIN = 4.5;     /* WCAG AA text — our "sharp contrast" floor */
  const CONTRAST_GOOD = 7;
  const HANDOFF_KEY = "grabUsdotHandoff";
  const EXPORT_DPI = 300;

  /* ══ 2. STATE ══ */
  const s = {
    name: "", usdot: "", mc: "", ca: "", gvwr: "", city: "",
    font: "impact", ink: "#000000", bg: "#FFFFFF",
    cut: "rect", size: SIZES[1]
  };
  const $ = id => document.getElementById(id);
  const root = $("grab-usdot-studio");
  if (!root) return;
  let PRODUCT = {};
  try { PRODUCT = JSON.parse(root.dataset.product || "{}"); } catch (e) { PRODUCT = {}; }

  /* ══ 3. VALIDATION ══ */
  const digits = v => String(v || "").replace(/\D+/g, "");
  function validate() {
    const errs = {};
    const name = s.name.trim();
    if (!name) errs.name = "Enter your legal name or single trade name as registered with FMCSA.";
    else if (name.length < 2) errs.name = "That looks too short to be a carrier name.";
    const dot = digits(s.usdot);
    if (!dot) errs.usdot = "Enter your USDOT number (digits only).";
    else if (dot.length > 8) errs.usdot = "USDOT numbers are at most 8 digits.";
    else if (/^0+$/.test(dot)) errs.usdot = "That isn't a valid USDOT number.";
    if (s.mc && digits(s.mc).length > 7) errs.mc = "MC numbers are up to 7 digits.";
    if (s.ca && digits(s.ca).length > 6) errs.ca = "CA numbers are up to 6 digits.";
    if (s.gvwr) {
      const g = parseInt(digits(s.gvwr), 10);
      if (!g || g < 1000 || g > 200000) errs.gvwr = "GVWR should be between 1,000 and 200,000 lbs.";
    }
    if (s.city && s.city.trim().length < 2) errs.city = "Use the form CITY, ST.";
    return errs;
  }
  function showErrors(errs) {
    ["name", "usdot", "mc", "ca", "gvwr", "city"].forEach(k => {
      const el = $("dotf-" + k + "-err"), input = $("dotf-" + k);
      if (!el) return;
      const touched = input && input.dataset.touched === "1";
      const show = !!errs[k] && (touched || (k !== "name" && k !== "usdot"));
      el.textContent = errs[k] || "";
      el.hidden = !show;
      if (input) input.setAttribute("aria-invalid", errs[k] ? "true" : "false");
    });
  }

  /* ══ 4. TEXT LINES ══ */
  function lines() {
    const out = [];
    const name = s.name.trim().toUpperCase();
    const dot = digits(s.usdot);
    out.push({ key: "name", text: name || "YOUR CARRIER NAME", weight: 1, placeholder: !name, required: true });
    out.push({ key: "usdot", text: "USDOT " + (dot || "1234567"), weight: 1.05, placeholder: !dot, required: true });
    if (digits(s.mc)) out.push({ key: "mc", text: "MC " + digits(s.mc), weight: 0.78 });
    if (digits(s.ca)) out.push({ key: "ca", text: "CA " + digits(s.ca), weight: 0.78 });
    if (digits(s.gvwr)) out.push({ key: "gvwr", text: "GVWR " + Number(digits(s.gvwr)).toLocaleString("en-US") + " LBS", weight: 0.78 });
    if (s.city.trim()) out.push({ key: "city", text: s.city.trim().toUpperCase(), weight: 0.78 });
    return out;
  }
  function fontDef() { return FONTS.find(f => f.id === s.font) || FONTS[0]; }
  function fontString(px) { const f = fontDef(); return f.weight + " " + px + "px " + f.family; }

  /* Measure with a scratch context at a reference size; everything scales linearly. */
  const scratch = document.createElement("canvas").getContext("2d");
  const REF_PX = 200;
  function metrics(text) {
    scratch.font = fontString(REF_PX);
    const m = scratch.measureText(text);
    const cap = scratch.measureText("H");
    const capH = (cap.actualBoundingBoxAscent || REF_PX * 0.72) / REF_PX; /* cap height per 1px font */
    const asc = (m.actualBoundingBoxAscent || REF_PX * 0.72) / REF_PX;
    const desc = (m.actualBoundingBoxDescent || REF_PX * 0.05) / REF_PX;
    return { width: m.width / REF_PX, capH, asc, desc };
  }

  /* Layout in inches: find the largest base font size (in) so every line fits
     the safe width and the whole stack fits the safe height. Returns per-line
     font size, cap height, and y positions (baseline) in inches. */
  function layout() {
    const L = lines(), W = s.size.w, H = s.size.h;
    const safeW = W - 2 * SAFE_IN, safeH = H - 2 * SAFE_IN;
    const ms = L.map(l => metrics(l.text));
    let base = Infinity;
    L.forEach((l, i) => { base = Math.min(base, safeW / (ms[i].width * l.weight)); });
    const stackAt = b => {
      let total = 0;
      L.forEach((l, i) => { total += ms[i].capH * b * l.weight; });
      const gaps = L.length > 1 ? (L.length - 1) * LINE_GAP * ms[1].capH * b * L[1].weight : 0;
      return total + gaps;
    };
    if (stackAt(base) > safeH) base = base * (safeH / stackAt(base));
    const rows = [];
    let y = (H - stackAt(base)) / 2;
    L.forEach((l, i) => {
      const fs = base * l.weight, cap = ms[i].capH * fs;
      y += cap;
      rows.push({ line: l, fontIn: fs, capIn: cap, widthIn: ms[i].width * fs, baselineIn: y, descIn: ms[i].desc * fs });
      y += LINE_GAP * ms[1].capH * base * L[1].weight;
    });
    return { rows, W, H };
  }

  /* ══ 5. CONTRAST (WCAG relative luminance) ══ */
  function hexToRgb(hex) {
    const h = String(hex).replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function luminance(hex) {
    const [r, g, b] = hexToRgb(hex).map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  function contrastRatio() {
    const a = luminance(s.ink), b = luminance(s.bg);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }

  /* ══ 6. RENDER (shared by preview + export) ══ */
  function renderTo(ctx, pxPerIn, opts) {
    const lay = layout();
    const Wpx = lay.W * pxPerIn, Hpx = lay.H * pxPerIn;
    ctx.save();
    ctx.clearRect(0, 0, Wpx, Hpx);
    if (s.cut === "rect") {
      ctx.fillStyle = s.bg;
      ctx.fillRect(0, 0, Wpx, Hpx);
    }
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    lay.rows.forEach(r => {
      const px = r.fontIn * pxPerIn;
      ctx.font = fontString(px);
      const x = Wpx / 2, y = r.baselineIn * pxPerIn;
      if (s.cut === "contour") {
        /* halo of background color so the contour tracer gets one solid
           lettering block, and colored backgrounds survive around the letters */
        ctx.lineJoin = "round";
        ctx.strokeStyle = s.bg;
        ctx.lineWidth = HALO_IN * 2 * pxPerIn;
        ctx.strokeText(r.line.text, x, y);
      }
      ctx.fillStyle = (opts && opts.placeholderTint && r.line.placeholder) ? "rgba(127,127,127,.55)" : s.ink;
      ctx.fillText(r.line.text, x, y);
    });
    ctx.restore();
    return lay;
  }

  function drawPreview() {
    const cv = $("dot-preview-canvas");
    if (!cv) return null;
    const ctx = cv.getContext("2d");
    const pad = 22;
    const scale = Math.min((cv.width - 2 * pad) / s.size.w, (cv.height - 2 * pad) / s.size.h);
    const Wpx = s.size.w * scale, Hpx = s.size.h * scale;
    const ox = (cv.width - Wpx) / 2, oy = (cv.height - Hpx) / 2;
    ctx.clearRect(0, 0, cv.width, cv.height);
    /* checkerboard = transparent */
    const b = 12;
    for (let y = 0; y < cv.height; y += b) for (let x = 0; x < cv.width; x += b) {
      ctx.fillStyle = ((x / b + y / b) % 2 === 0) ? "#efefef" : "#e3e3e3";
      ctx.fillRect(x, y, b, b);
    }
    /* decal area */
    ctx.save();
    ctx.translate(ox, oy);
    const art = document.createElement("canvas");
    art.width = Math.max(1, Math.round(Wpx)); art.height = Math.max(1, Math.round(Hpx));
    const lay = renderTo(art.getContext("2d"), scale, { placeholderTint: true });
    ctx.drawImage(art, 0, 0);
    ctx.restore();
    /* cut line + safe margin */
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#FF2E88";
    if (s.cut === "rect") ctx.strokeRect(ox, oy, Wpx, Hpx);
    else {
      /* approximate contour: rounded box around the lettering block + halo */
      const top = lay.rows[0].baselineIn - lay.rows[0].capIn - HALO_IN, bot = lay.rows[lay.rows.length - 1].baselineIn + lay.rows[lay.rows.length - 1].descIn + HALO_IN;
      const wmax = Math.max.apply(null, lay.rows.map(r => r.widthIn)) + 2 * HALO_IN;
      const x0 = ox + (s.size.w - wmax) / 2 * scale, y0 = oy + top * scale, rw = wmax * scale, rh = (bot - top) * scale, rr = HALO_IN * scale;
      ctx.beginPath();
      ctx.moveTo(x0 + rr, y0); ctx.lineTo(x0 + rw - rr, y0); ctx.quadraticCurveTo(x0 + rw, y0, x0 + rw, y0 + rr);
      ctx.lineTo(x0 + rw, y0 + rh - rr); ctx.quadraticCurveTo(x0 + rw, y0 + rh, x0 + rw - rr, y0 + rh);
      ctx.lineTo(x0 + rr, y0 + rh); ctx.quadraticCurveTo(x0, y0 + rh, x0, y0 + rh - rr);
      ctx.lineTo(x0, y0 + rr); ctx.quadraticCurveTo(x0, y0, x0 + rr, y0); ctx.closePath(); ctx.stroke();
      ctx.strokeStyle = "#8a8a8a"; ctx.setLineDash([3, 5]); ctx.strokeRect(ox, oy, Wpx, Hpx);
    }
    ctx.strokeStyle = "#00AEEF"; ctx.setLineDash([3, 4]);
    ctx.strokeRect(ox + SAFE_IN * scale, oy + SAFE_IN * scale, Wpx - 2 * SAFE_IN * scale, Hpx - 2 * SAFE_IN * scale);
    ctx.restore();
    return lay;
  }

  /* ══ 7. CHECKS ══ */
  function checks(lay, errs) {
    const dotRow = lay.rows.find(r => r.line.key === "usdot");
    const nameRow = lay.rows[0];
    const cap = dotRow ? dotRow.capIn : 0;
    const ratio = contrastRatio();
    const list = [];
    list.push({ ok: !errs.name, warn: false, text: errs.name ? "Legal name or single trade name is required on the power unit." : "Name line: \u201C" + nameRow.line.text + "\u201D — " + nameRow.capIn.toFixed(2) + "\u2033 letters" });
    list.push({ ok: !errs.usdot, warn: false, text: errs.usdot ? "USDOT number is required, with \u201CUSDOT\u201D immediately before it." : "\u201CUSDOT\u201D precedes the number \u2713" });
    if (cap >= CAP_STANDARD) list.push({ ok: true, text: "Legibility: USDOT line is " + cap.toFixed(2) + "\u2033 tall — meets the 2\u2033 industry standard for 50 ft daylight legibility" });
    else if (cap >= CAP_FLOOR) list.push({ ok: true, warn: true, text: "Legibility: USDOT line is " + cap.toFixed(2) + "\u2033 tall — under the 2\u2033 standard. Choose a bigger size, the Tall lettering, or drop an optional line." });
    else list.push({ ok: false, text: "Legibility: USDOT line is only " + cap.toFixed(2) + "\u2033 tall. Pick a larger size or remove optional lines — we won\u2019t print below " + CAP_FLOOR + "\u2033." });
    if (ratio >= CONTRAST_GOOD) list.push({ ok: true, text: "Contrast " + ratio.toFixed(1) + ":1 — sharp" });
    else if (ratio >= CONTRAST_MIN) list.push({ ok: true, warn: true, text: "Contrast " + ratio.toFixed(1) + ":1 — acceptable; darker letters or a lighter background would be sharper" });
    else list.push({ ok: false, text: "Contrast " + ratio.toFixed(1) + ":1 — too low. Letters must contrast sharply with the background (aim for 4.5:1 or more)." });
    list.push({ ok: true, info: true, text: "Required on both sides of the power unit — order in sets of 2." });
    const blocking = list.some(c => !c.ok);
    return { list, blocking, cap, ratio };
  }
  function renderChecks(res) {
    const ul = $("dot-checklist");
    if (!ul) return;
    ul.innerHTML = "";
    res.list.forEach(c => {
      const li = document.createElement("li");
      li.className = c.info ? "is-info" : (c.ok ? (c.warn ? "is-warn" : "is-ok") : "is-bad");
      li.innerHTML = '<span class="dot-check__mark" aria-hidden="true">' + (c.info ? "i" : c.ok ? (c.warn ? "!" : "\u2713") : "\u2715") + '</span><span>' + c.text.replace(/</g, "&lt;") + "</span>";
      ul.appendChild(li);
    });
    const contrast = $("dot-contrast");
    if (contrast) {
      contrast.textContent = "Contrast " + res.ratio.toFixed(1) + ":1" + (res.ratio >= CONTRAST_GOOD ? " — sharp" : res.ratio >= CONTRAST_MIN ? " — acceptable" : " — too low");
      contrast.className = "dot-contrast " + (res.ratio >= CONTRAST_GOOD ? "is-ok" : res.ratio >= CONTRAST_MIN ? "is-warn" : "is-bad");
    }
  }

  /* ══ 8. MAIN REFRESH ══ */
  function refresh() {
    const errs = validate();
    showErrors(errs);
    const lay = drawPreview();
    if (!lay) return;
    const res = checks(lay, errs);
    renderChecks(res);
    const note = $("dot-preview-note");
    if (note) note.textContent = s.size.w + "\u2033 \u00D7 " + s.size.h + "\u2033 \u2022 " + (s.cut === "rect" ? "rectangle" : "contour cut") + " \u2022 " + fontDef().label + " lettering \u2022 USDOT line " + res.cap.toFixed(2) + "\u2033 tall";
    const ready = !res.blocking;
    const order = $("dot-order"), label = $("dot-order-label"), err = $("dot-order-err");
    const dl1 = $("dot-dl-png"), dl2 = $("dot-dl-svg");
    const filled = !errs.name && !errs.usdot;
    if (dl1) dl1.disabled = !filled;
    if (dl2) dl2.disabled = !filled;
    if (order) {
      order.disabled = !ready || !PRODUCT.url;
      if (label) label.textContent = !filled ? "Enter your details to order" : !ready ? "Fix the checklist to order" : !PRODUCT.url ? "Ordering coming soon" : "Order this decal" + (PRODUCT.from ? " — sets from " + PRODUCT.from : "");
    }
    if (err) err.hidden = true;
  }

  /* ══ 9. EXPORT ══ */
  /* v1.1: attribution. crc32/pngWithText embed tEXt metadata chunks into the
     exported PNG bytes (pixels untouched); the SVG gains a deletable Info
     layer OUTSIDE the decal. Free downloads only — the order hand-off is
     byte-identical to v1.0. */
  const PNG_META = [
    ["Software", "Grabatee USDOT Decal Studio (free) - grabatee.com"],
    ["Comment", "Made with the free Grabatee USDOT decal designer: grabatee.com/pages/usdot-number-decals"]
  ];
  function crc32(bytes, start, end) {
    let table = crc32.t;
    if (!table) {
      table = crc32.t = [];
      for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1); table[n] = c >>> 0; }
    }
    let c = 0xFFFFFFFF;
    for (let i = start; i < end; i++) c = table[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
  }
  function pngWithText(bytes, pairs) {
    /* every valid PNG starts sig(8) + IHDR chunk(25); insert tEXt at offset 33 */
    const chunks = [];
    pairs.forEach(p => {
      const data = p[0] + "\0" + p[1], n = data.length, ch = new Uint8Array(12 + n);
      ch[0] = (n >>> 24) & 255; ch[1] = (n >>> 16) & 255; ch[2] = (n >>> 8) & 255; ch[3] = n & 255;
      ch[4] = 116; ch[5] = 69; ch[6] = 88; ch[7] = 116;
      for (let i = 0; i < n; i++) ch[8 + i] = data.charCodeAt(i) & 255;
      const crc = crc32(ch, 4, 8 + n);
      ch[8 + n] = (crc >>> 24) & 255; ch[9 + n] = (crc >>> 16) & 255; ch[10 + n] = (crc >>> 8) & 255; ch[11 + n] = crc & 255;
      chunks.push(ch);
    });
    const extra = chunks.reduce((a, c) => a + c.length, 0);
    const out = new Uint8Array(bytes.length + extra);
    out.set(bytes.subarray(0, 33), 0);
    let pos = 33;
    chunks.forEach(c => { out.set(c, pos); pos += c.length; });
    out.set(bytes.subarray(33), pos);
    return out;
  }
  function pngHref(cv) {
    try {
      const b64 = cv.toDataURL("image/png").split(",")[1], bin = atob(b64), bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([pngWithText(bytes, PNG_META)], { type: "image/png" }));
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      return url;
    } catch (e) { return cv.toDataURL("image/png"); }
  }
  function baseName() {
    const dot = digits(s.usdot) || "usdot";
    return "grabatee-usdot-" + dot + "-" + s.size.value.replace("x", "x") + "-" + s.cut;
  }
  function renderPNG(dpi) {
    const cv = document.createElement("canvas");
    cv.width = Math.round(s.size.w * dpi);
    cv.height = Math.round(s.size.h * dpi);
    renderTo(cv.getContext("2d"), dpi, {});
    return cv;
  }
  function download(href, name) {
    const a = document.createElement("a");
    a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  }
  function esc(t) { return String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;"); }
  function buildSVG() {
    const lay = layout(), f = fontDef(), W = lay.W, H = lay.H;
    const CREDIT_IN = 0.42, docH = (H + CREDIT_IN).toFixed(2);
    let out = '<?xml version="1.0" encoding="UTF-8"?>\n<!-- Made with the free Grabatee USDOT decal designer - grabatee.com/pages/usdot-number-decals. Text is live; fonts: ' + esc(f.family) + '. Sizes in inches. The #Info credit layer sits BELOW the decal, outside the cut - delete that layer before production if you do not want it. -->\n' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + 'in" height="' + docH + 'in" viewBox="0 0 ' + W + ' ' + docH + '">\n';
    if (s.cut === "rect") out += '  <rect width="' + W + '" height="' + H + '" fill="' + s.bg + '"/>\n';
    out += '  <g font-family=' + "'" + esc(f.family.replace(/"/g, "")) + "'" + ' font-weight="' + f.weight + '" text-anchor="middle" fill="' + s.ink + '">\n';
    lay.rows.forEach(r => {
      const halo = s.cut === "contour" ? ' stroke="' + s.bg + '" stroke-width="' + (HALO_IN * 2).toFixed(3) + '" stroke-linejoin="round" paint-order="stroke"' : "";
      out += '    <text x="' + (W / 2).toFixed(3) + '" y="' + r.baselineIn.toFixed(3) + '" font-size="' + r.fontIn.toFixed(4) + '"' + halo + '>' + esc(r.line.text) + '</text>\n';
    });
    out += '  </g>\n';
    out += '  <g id="Info" font-family="Arial, Helvetica, sans-serif">\n' +
      '    <line x1="0" y1="' + H + '" x2="' + W + '" y2="' + H + '" stroke="#B9B9B9" stroke-width="0.012" stroke-dasharray="0.1 0.07"/>\n' +
      '    <text x="' + (W / 2).toFixed(3) + '" y="' + (H + 0.27).toFixed(2) + '" text-anchor="middle" font-size="0.14" fill="#8A8A8A">Made free with the Grabatee USDOT decal designer \u2014 grabatee.com</text>\n' +
      '  </g>\n</svg>\n';
    return out;
  }

  /* ══ 10. HAND-OFF ══ */
  function handoff() {
    const errs = validate();
    const lay = layout();
    const res = checks(lay, errs);
    const err = $("dot-order-err");
    if (res.blocking || !PRODUCT.url) { if (err) { err.textContent = "Fix the checklist items above first."; err.hidden = false; } return; }
    const btn = $("dot-order"), label = $("dot-order-label");
    if (btn) btn.disabled = true;
    if (label) label.textContent = "Preparing your decal\u2026";
    setTimeout(() => {
      let dataURL = null, dpi = EXPORT_DPI;
      const attempts = [300, 200, 150];
      for (const d of attempts) {
        try {
          const url = renderPNG(d).toDataURL("image/png");
          if (url.length < 4.2e6) { dataURL = url; dpi = d; break; }
        } catch (e) { /* try smaller */ }
      }
      if (!dataURL) {
        if (err) { err.textContent = "Couldn\u2019t prepare the file in this browser. Download the PNG instead and upload it on the order page."; err.hidden = false; }
        if (btn) btn.disabled = false;
        if (label) label.textContent = "Order this decal";
        return;
      }
      const payload = {
        v: 1, created: Date.now(), type: "usdot",
        sizeValue: s.size.value, fileName: baseName() + ".png", dpi: dpi, dataURL: dataURL,
        usdotNumber: digits(s.usdot), carrierName: s.name.trim().toUpperCase(),
        lines: lay.rows.map(r => r.line.text),
        cutStyle: s.cut === "rect" ? "Rectangle" : "Contour cut",
        letterHeight: res.cap.toFixed(2) + '"', contrast: res.ratio.toFixed(1) + ":1",
        ink: s.ink, bg: s.bg, font: fontDef().label
      };
      try { sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(payload)); }
      catch (e) {
        if (err) { err.textContent = "Your browser blocked the hand-off. Download the PNG and upload it on the order page."; err.hidden = false; }
        if (btn) btn.disabled = false;
        if (label) label.textContent = "Order this decal";
        return;
      }
      window.location.href = PRODUCT.url + (PRODUCT.url.indexOf("?") >= 0 ? "&" : "?") + "usdot=1";
    }, 30);
  }

  /* ══ 11. UI BUILD ══ */
  function bindInput(id, key, opts) {
    const el = $(id);
    if (!el) return;
    el.addEventListener("input", () => {
      let v = el.value;
      if (opts && opts.digits) { v = digits(v); if (el.value !== v) el.value = v; }
      s[key] = v;
      refresh();
    });
    el.addEventListener("blur", () => { el.dataset.touched = "1"; refresh(); });
  }
  function buildSwatches(id, list, key, customId) {
    const wrap = $(id);
    if (!wrap) return;
    wrap.innerHTML = "";
    list.forEach(c => {
      const b = document.createElement("button");
      b.type = "button"; b.className = "dot-swatch"; b.style.background = c.hex; b.dataset.hex = c.hex;
      b.setAttribute("aria-label", c.name); b.title = c.name;
      b.addEventListener("click", () => { s[key] = c.hex; const ci = $(customId); if (ci) ci.value = c.hex; syncSwatches(); refresh(); });
      wrap.appendChild(b);
    });
    const ci = $(customId);
    if (ci) ci.addEventListener("input", () => { s[key] = ci.value.toUpperCase(); syncSwatches(); refresh(); });
  }
  function syncSwatches() {
    document.querySelectorAll("#dot-ink-swatches .dot-swatch").forEach(b => b.classList.toggle("active", b.dataset.hex.toUpperCase() === s.ink.toUpperCase()));
    document.querySelectorAll("#dot-bg-swatches .dot-swatch").forEach(b => b.classList.toggle("active", b.dataset.hex.toUpperCase() === s.bg.toUpperCase()));
  }
  function buildFonts() {
    const wrap = $("dot-fonts");
    if (!wrap) return;
    wrap.innerHTML = "";
    FONTS.forEach(f => {
      const b = document.createElement("button");
      b.type = "button"; b.className = "dot-font" + (f.id === s.font ? " active" : ""); b.dataset.font = f.id;
      b.innerHTML = '<span class="dot-font__sample" style="font-family:' + f.family.replace(/"/g, "&quot;") + ';font-weight:' + f.weight + '">' + f.sample + '</span><span class="dot-font__label">' + f.label + '</span><span class="dot-font__note">' + f.note + '</span>';
      b.setAttribute("aria-pressed", f.id === s.font ? "true" : "false");
      b.addEventListener("click", () => {
        s.font = f.id;
        document.querySelectorAll("#dot-fonts .dot-font").forEach(x => { const on = x.dataset.font === f.id; x.classList.toggle("active", on); x.setAttribute("aria-pressed", on ? "true" : "false"); });
        refresh();
      });
      wrap.appendChild(b);
    });
  }
  function buildSizes() {
    const wrap = $("dot-sizes");
    if (!wrap) return;
    wrap.innerHTML = "";
    SIZES.forEach(z => {
      const b = document.createElement("button");
      b.type = "button"; b.className = "dot-size" + (z.value === s.size.value ? " active" : ""); b.dataset.size = z.value;
      b.innerHTML = z.w + "\u2033 \u00D7 " + z.h + "\u2033";
      b.setAttribute("aria-pressed", z.value === s.size.value ? "true" : "false");
      b.addEventListener("click", () => {
        s.size = z;
        document.querySelectorAll("#dot-sizes .dot-size").forEach(x => { const on = x.dataset.size === z.value; x.classList.toggle("active", on); x.setAttribute("aria-pressed", on ? "true" : "false"); });
        refresh();
      });
      wrap.appendChild(b);
    });
  }

  function init() {
    bindInput("dotf-name", "name");
    bindInput("dotf-usdot", "usdot", { digits: true });
    bindInput("dotf-mc", "mc", { digits: true });
    bindInput("dotf-ca", "ca", { digits: true });
    bindInput("dotf-gvwr", "gvwr", { digits: true });
    bindInput("dotf-city", "city");
    buildFonts();
    buildSwatches("dot-ink-swatches", INK_SWATCHES, "ink", "dot-ink-custom");
    buildSwatches("dot-bg-swatches", BG_SWATCHES, "bg", "dot-bg-custom");
    syncSwatches();
    buildSizes();
    document.querySelectorAll('input[name="dot-cut"]').forEach(r => r.addEventListener("change", () => {
      if (r.checked) { s.cut = r.value; const bl = $("dot-bg-label"); if (bl) bl.textContent = s.cut === "rect" ? "Background color" : "Halo color (around the letters)"; refresh(); }
    }));
    const png = $("dot-dl-png"), svg = $("dot-dl-svg"), order = $("dot-order");
    if (png) png.addEventListener("click", () => { try { download(pngHref(renderPNG(EXPORT_DPI)), baseName() + "-300dpi.png"); } catch (e) { alert("Could not export the PNG in this browser."); } });
    if (svg) svg.addEventListener("click", () => { const blob = new Blob([buildSVG()], { type: "image/svg+xml" }); const u = URL.createObjectURL(blob); download(u, baseName() + ".svg"); setTimeout(() => URL.revokeObjectURL(u), 2000); });
    if (order) order.addEventListener("click", handoff);
    /* fonts may load late; re-measure once they're ready */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh).catch(() => {});
    refresh();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

